import { useEffect, useMemo, useRef, useState } from 'react';

// Gráfica de puntos y líneas (SVG, sin librerías). Recibe puntos
// [{ date: 'YYYY-MM-DD', value }] ya ordenados por fecha. Un `value` null
// significa "sin dato ese día": deja hueco en la línea (no se pinta ni el
// punto ni el tramo) pero el día sigue ocupando su sitio en el eje, para que
// el rango elegido se vea entero aunque el histórico sea corto. Escala Y
// desde 0 con ticks "bonitos", área suave bajo la línea y tooltip al pasar
// el ratón. El ancho se mide con ResizeObserver para no deformar nada.

const PAD = { top: 14, right: 14, bottom: 26, left: 40 };
const MIN_HEIGHT = 180;

function niceMax(max) {
  if (max <= 0) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const normalized = max / magnitude;
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const unit = (step * magnitude) / 4;
  return Math.ceil(max / unit) * unit;
}

function formatDay(date, withYear = false) {
  const d = new Date(`${date}T00:00:00`);
  if (!Number.isFinite(d.getTime())) return date;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
}

function formatValue(value) {
  return Number(value || 0).toLocaleString('es-ES');
}

function hasValue(point) {
  return point && point.value !== null && point.value !== undefined && Number.isFinite(Number(point.value));
}

export default function LineChart({
  points = [],
  color = '#3b82f6',
  height = 200,
  emptyText = 'Sin datos todavia',
  valueLabel = ''
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth || 0);
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const chartHeight = Math.max(MIN_HEIGHT, height);
  const geometry = useMemo(() => {
    const innerWidth = Math.max(0, width - PAD.left - PAD.right);
    const innerHeight = chartHeight - PAD.top - PAD.bottom;
    const values = points.filter(hasValue).map((p) => Number(p.value));
    const max = niceMax(Math.max(0, ...values));
    const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;
    const coords = points.map((p, i) => {
      const gap = !hasValue(p);
      return {
        x: PAD.left + (points.length > 1 ? i * stepX : innerWidth / 2),
        y: gap ? null : PAD.top + innerHeight - (Number(p.value) / max) * innerHeight,
        gap,
        point: p
      };
    });
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
      value: Math.round(max * f),
      y: PAD.top + innerHeight - f * innerHeight
    }));
    const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(innerWidth / 70))));
    // Tramos continuos (los huecos parten la línea y el área).
    const segments = [];
    let current = [];
    for (const c of coords) {
      if (c.gap) {
        if (current.length) segments.push(current);
        current = [];
      } else {
        current.push(c);
      }
    }
    if (current.length) segments.push(current);
    return { innerWidth, innerHeight, max, coords, ticks, labelEvery, segments, hasAnyValue: values.length > 0 };
  }, [points, width, chartHeight]);

  const baseline = (PAD.top + geometry.innerHeight).toFixed(1);
  const linePaths = geometry.segments.map((segment) =>
    segment.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')
  );
  const areaPaths = geometry.segments.map((segment, i) =>
    `${linePaths[i]} L${segment[segment.length - 1].x.toFixed(1)},${baseline} L${segment[0].x.toFixed(1)},${baseline} Z`
  );
  const gradientId = useMemo(() => `lc-${Math.random().toString(36).slice(2, 8)}`, []);

  function onMouseMove(event) {
    if (!geometry.coords.length) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    let best = null;
    let bestDist = Infinity;
    geometry.coords.forEach((c, i) => {
      const dist = Math.abs(c.x - x);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setHover(best);
  }

  const hovered = hover != null ? geometry.coords[hover] : null;
  const tooltipLeft = hovered ? Math.min(Math.max(hovered.x, 70), Math.max(70, width - 70)) : 0;

  return (
    <div ref={containerRef} className="relative w-full select-none" style={{ height: chartHeight }}>
      {(points.length === 0 || !geometry.hasAnyValue) && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-default-400">{emptyText}</div>
      )}
      {width > 0 && points.length > 0 && (
        <svg
          width={width}
          height={chartHeight}
          className="block"
          onMouseMove={onMouseMove}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {geometry.ticks.map((tick) => (
            <g key={tick.y}>
              <line
                x1={PAD.left}
                x2={PAD.left + geometry.innerWidth}
                y1={tick.y}
                y2={tick.y}
                stroke="currentColor"
                strokeOpacity="0.08"
                strokeDasharray={tick.value === 0 ? undefined : '3 4'}
              />
              <text x={PAD.left - 8} y={tick.y + 3.5} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.45">
                {formatValue(tick.value)}
              </text>
            </g>
          ))}
          {areaPaths.map((d, i) => <path key={`a${i}`} d={d} fill={`url(#${gradientId})`} />)}
          {linePaths.map((d, i) => (
            <path key={`l${i}`} d={d} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {geometry.coords.map((c, i) => {
            const showLabel = i === 0 || i === geometry.coords.length - 1 || i % geometry.labelEvery === 0;
            const isHover = hover === i;
            return (
              <g key={c.point.date}>
                {!c.gap && (
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={isHover ? 5 : 3}
                    fill={isHover ? color : '#fff'}
                    stroke={color}
                    strokeWidth="2"
                  />
                )}
                {showLabel && (
                  <text
                    x={c.x}
                    y={chartHeight - 8}
                    textAnchor={i === 0 ? 'start' : i === geometry.coords.length - 1 ? 'end' : 'middle'}
                    fontSize="10"
                    fill="currentColor"
                    fillOpacity="0.45"
                  >
                    {formatDay(c.point.date).replace('.', '')}
                  </text>
                )}
              </g>
            );
          })}
          {hovered && (
            <line
              x1={hovered.x}
              x2={hovered.x}
              y1={PAD.top}
              y2={PAD.top + geometry.innerHeight}
              stroke={color}
              strokeOpacity="0.35"
              strokeDasharray="3 3"
            />
          )}
        </svg>
      )}
      {hovered && (
        <div
          className="pointer-events-none absolute top-1 z-20 -translate-x-1/2 rounded-lg border border-default-200 bg-content1 px-3 py-2 text-left shadow-xl"
          style={{ left: tooltipLeft }}
        >
          <div className="text-[11px] font-medium uppercase tracking-wide text-default-500">{formatDay(hovered.point.date, true)}</div>
          <div className="mt-0.5 text-lg font-semibold leading-none text-foreground">
            {hovered.gap ? <span className="text-sm text-default-400">sin datos</span> : formatValue(hovered.point.value)}
          </div>
          {valueLabel && !hovered.gap && <div className="mt-1 text-[11px] text-default-500">{valueLabel}</div>}
        </div>
      )}
    </div>
  );
}
